(() => {
  'use strict';

  const injectMellerSearchModalStyles = () => {
    if (document.getElementById('meller-search-modal-style')) return;

    const template = document.getElementById('meller-search-modal-style-template');
    const templateStyle = template?.content?.querySelector('style');

    if (!templateStyle) return;

    const style = templateStyle.cloneNode(true);
    style.id = 'meller-search-modal-style';

    document.head.appendChild(style);
    template.remove();
  };

  const setupMellerSearchModal = () => {
    const modal = document.querySelector('#search-modal');
    const dialog = modal?.querySelector('dialog');

    if (!modal || !dialog || modal.dataset.mellerSearchSetup === 'true') return;

    modal.dataset.mellerSearchSetup = 'true';

    let initialized = false;
    let syncTimer = null;
    let resultsObserver = null;
    let keydownBound = false;
    let submittingSearch = false;
    let lastSearchQuery = '';

    const getInput = () => dialog.querySelector('.search-input');
    const getResults = () => dialog.querySelector('[ref="predictiveSearchResults"]');
    const getResetButton = () => dialog.querySelector('.predictive-search__reset-button');
    const getComponent = () => dialog.querySelector('predictive-search-component');

    const isModalOpen = () => dialog.hasAttribute('open');

    const getForm = () => dialog.querySelector('form.predictive-search-form');
    const getViewAllButton = () => dialog.querySelector('[ref="viewAllButton"], .predictive-search__search-button');

    const closeSearchDialog = () => {
      if (!isModalOpen()) return;

      if (typeof modal.closeDialog === 'function') {
        modal.closeDialog();
        return;
      }

      try {
        dialog.close();
      } catch (_error) {
        dialog.removeAttribute('open');
      }
    };

    const getCurrentSearchQuery = (fallbackQuery = '') => {
      const input = getInput();
      const query = input?.value?.trim() || fallbackQuery || lastSearchQuery || '';

      return query.trim();
    };

    const submitExactInputSearch = (event, fallbackQuery = '') => {
      const form = getForm();
      const query = getCurrentSearchQuery(fallbackQuery);

      if (!form || !query) return false;

      if (event) {
        event.preventDefault();
        event.stopPropagation();

        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }
      }

      submittingSearch = true;
      lastSearchQuery = query;

      const url = new URL(form.getAttribute('action') || window.Theme?.routes?.search_url || '/search', window.location.origin);
      const params = new URLSearchParams(new FormData(form));

      params.set('q', query);
      params.set('options[prefix]', 'last');

      url.search = params.toString();

      window.location.assign(url.toString());

      return true;
    };

    const isMobileViewport = () => {
      return window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
    };

    const clearSyncTimer = () => {
      if (!syncTimer) return;

      window.clearTimeout(syncTimer);
      syncTimer = null;
    };

    const blurSearchInput = () => {
      const input = getInput();

      if (input && document.activeElement === input) {
        input.blur();
      }
    };

    const unlockMobileViewport = () => {
      if (!isMobileViewport()) return;

      blurSearchInput();

      document.documentElement.style.removeProperty('overflow');
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('width');
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('touch-action');

      window.setTimeout(() => {
        window.scrollTo(window.scrollX, window.scrollY);
      }, 60);
    };

    const setEmptyState = () => {
      const results = getResults();

      if (results) {
        results.classList.add('predictive-search-form__content--empty');
      }
    };

    const removeEmptyState = () => {
      const results = getResults();

      if (results) {
        results.classList.remove('predictive-search-form__content--empty');
      }
    };

    const syncResultsState = () => {
      const input = getInput();
      const results = getResults();

      if (!input || !results) return;

      const hasText = input.value.trim().length > 0;
      const hasResultsMarkup = results.innerHTML.trim().length > 0;

      if (hasText && hasResultsMarkup) {
        removeEmptyState();
      } else {
        setEmptyState();
      }
    };

    const scheduleSyncResultsState = () => {
      clearSyncTimer();

      syncTimer = window.setTimeout(() => {
        syncTimer = null;
        syncResultsState();
      }, 120);
    };

    const disconnectResultsObserver = () => {
      if (resultsObserver) {
        resultsObserver.disconnect();
        resultsObserver = null;
      }
    };

    const connectResultsObserver = () => {
      if (resultsObserver || !('MutationObserver' in window)) return;

      const results = getResults();

      if (!results) return;

      resultsObserver = new MutationObserver(scheduleSyncResultsState);

      resultsObserver.observe(results, {
        childList: true,
        subtree: true
      });
    };

    const resetPredictiveSearch = () => {
      if (submittingSearch) return;
      const input = getInput();
      const results = getResults();
      const resetButton = getResetButton();
      const component = getComponent();

      clearSyncTimer();
      disconnectResultsObserver();
      blurSearchInput();

      if (input) {
        input.value = '';
        input.setAttribute('aria-expanded', 'false');
      }

      if (results) {
        results.innerHTML = '';
        results.classList.add('predictive-search-form__content--empty');
      }

      if (resetButton) {
        resetButton.hidden = true;
      }

      if (component) {
        component.classList.remove('predictive-search--expanded');
      }

      window.setTimeout(unlockMobileViewport, 80);
    };

    const resetWithDelay = () => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(resetPredictiveSearch);
      });
    };

    const bindInputEvents = () => {
      const input = getInput();

      if (!input || input.dataset.mellerSearchInputReady === 'true') return;

      input.dataset.mellerSearchInputReady = 'true';

      input.setAttribute('autocomplete', 'off');
      input.setAttribute('autocorrect', 'off');
      input.setAttribute('autocapitalize', 'off');
      input.setAttribute('spellcheck', 'false');

      input.addEventListener(
        'keydown',
        (event) => {
          if (event.key !== 'Enter' || event.isComposing || event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) {
            return;
          }

          submitExactInputSearch(event);
        },
        { capture: true }
      );

      const form = getForm();

      if (form && form.dataset.mellerExactSubmitReady !== 'true') {
        form.dataset.mellerExactSubmitReady = 'true';

        form.addEventListener(
          'submit',
          (event) => {
            submitExactInputSearch(event);
          },
          { capture: true }
        );
      }

      const viewAllButton = getViewAllButton();

      if (viewAllButton && viewAllButton.dataset.mellerExactViewAllReady !== 'true') {
        viewAllButton.dataset.mellerExactViewAllReady = 'true';
        viewAllButton.setAttribute('type', 'button');

        const submitFromViewAll = (event) => {
          const inputNow = getInput();
          const query = inputNow?.value?.trim() || lastSearchQuery || '';

          if (query) {
            lastSearchQuery = query;
          }

          submitExactInputSearch(event, query);
        };

        viewAllButton.addEventListener('pointerdown', submitFromViewAll, {
          capture: true
        });

        viewAllButton.addEventListener('mousedown', submitFromViewAll, {
          capture: true
        });

        viewAllButton.addEventListener('touchstart', submitFromViewAll, {
          capture: true
        });

        viewAllButton.addEventListener('click', submitFromViewAll, {
          capture: true
        });
      }

      input.addEventListener('input', () => {
        if (input.value.trim().length === 0) {
          setEmptyState();
        } else {
          connectResultsObserver();
          scheduleSyncResultsState();
          window.setTimeout(syncResultsState, 300);
        }
      });

      input.addEventListener('focus', () => {
        if (input.value.trim().length === 0) {
          setEmptyState();
        } else {
          connectResultsObserver();
          scheduleSyncResultsState();
        }
      });
    };

    const bindModalEvents = () => {
      if (initialized) return;

      initialized = true;

      dialog.addEventListener('close', resetPredictiveSearch);

      modal.addEventListener('click', (event) => {
        const target = event.target;

        if (!(target instanceof Element)) return;

        if (target.closest('.predictive-search__close-modal-button')) {
          resetWithDelay();
          return;
        }

        if (target.closest('[data-meller-search-modal-inner]')) {
          return;
        }

        if (target === dialog || target === modal) {
          closeSearchDialog();
          resetWithDelay();
        }
      });

      bindInputEvents();
      setEmptyState();
    };

    const bindKeydownWhileOpen = () => {
      if (keydownBound) return;

      keydownBound = true;

      document.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Escape' && isModalOpen()) {
            resetWithDelay();
          }
        },
        { passive: true }
      );
    };

    const prepareSearch = () => {
      injectMellerSearchModalStyles();
      bindModalEvents();

      if (isModalOpen()) {
        bindKeydownWhileOpen();
        connectResultsObserver();
        bindInputEvents();
        scheduleSyncResultsState();
      }
    };

    const observeOpenState = () => {
      if (!('MutationObserver' in window)) return;

      const openObserver = new MutationObserver(() => {
        if (isModalOpen()) {
          prepareSearch();
        } else {
          clearSyncTimer();
          disconnectResultsObserver();
        }
      });

      openObserver.observe(dialog, {
        attributes: true,
        attributeFilter: ['open']
      });
    };

    observeOpenState();

    document.addEventListener(
      'pointerdown',
      (event) => {
        const target = event.target;

        if (!(target instanceof Element)) return;

        if (
          target.closest('[aria-controls="search-modal"], .search-modal__button, .search-action, button[type="search"]')
        ) {
          prepareSearch();
        }
      },
      { capture: true, passive: true }
    );

    document.addEventListener(
      'focusin',
      (event) => {
        const target = event.target;

        if (!(target instanceof Element)) return;

        if (target.closest('#search-modal')) {
          prepareSearch();
        }
      },
      { passive: true }
    );

    if (isModalOpen()) {
      prepareSearch();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupMellerSearchModal, { once: true });
  } else {
    setupMellerSearchModal();
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', setupMellerSearchModal);
  }
})();
